// 공지 카테고리 정규화 + 파생 목록 필터링 + 마감 D-day 계산 (순수 함수)
//
// 화면 표시 카테고리:
// 전체 | 학사 | 장학 | 취업·진로 | 창업·공모전 | 행사·모집 | 생활·시설 | 기타

import { isScholarshipNotice, normalizeForSearch } from './noticeScholarshipFilter'
import { noticeReadKey } from './noticeReadKeys'

export const NOTICE_DISPLAY_CATEGORIES = [
  '전체',
  '즐겨찾기',
  '학사',
  '장학',
  '취업·진로',
  '창업·공모전',
  '행사·모집',
  '생활·시설',
  '기타',
]

// 원본 category가 그대로 표시 카테고리로 인정되는 값
const DISPLAY_CATEGORY_SET = new Set([
  '학사',
  '장학',
  '취업·진로',
  '창업·공모전',
  '행사·모집',
  '생활·시설',
  '기타',
])

// 첫 매치 우선 — 구체적인 카테고리를 앞에 둔다.
const CATEGORY_KEYWORD_RULES = [
  [
    '창업·공모전',
    [
      // 창업
      '창업', '창업동아리', '창업지원', '스타트업', '예비창업', '사업계획', 'ir',
      // 공모전
      '공모전', '경진대회', '해커톤', '챌린지', '아이디어 대회', '아이디어공모',
    ],
  ],
  [
    '취업·진로',
    ['취업', '채용', '인턴', '진로', '현장실습', '일경험', '직무', '산학', '일자리', '잡페어', '구직', '자격증'],
  ],
  [
    '생활·시설',
    [
      '시설', '전산', '네트워크', '정전', '공사', '점검', '서버', '홈페이지', '와이파이',
      '시스템', '정보화', '변전', '설비', '통신', '냉난방', '엘리베이터', '인터넷', '케이블',
      '식당', '기숙사', '보건', '주차', '생활관',
    ],
  ],
  [
    '행사·모집',
    [
      '모집', '행사', '대회', '공모', '특강', '세미나', '워크숍', '워크샵', '캠프',
      '공연', '축제', '설명회', '박람회', '포럼', '부스', '선발', '신입생', '초빙', '활동',
    ],
  ],
  [
    '학사',
    [
      '수강', '성적', '학적', '등록', '졸업', '계절학기', '수업', '시험', '휴학',
      '복학', '재수강', '학점', '강의', '시간표', '수료', '전공', '편입', '학사', '증명서', '정정', '열람',
    ],
  ],
].map(([cat, keys]) => [cat, keys.map(normalizeForSearch)])

/**
 * 공지 1건을 화면 표시용 카테고리로 분류한다.
 * 우선순위: 원본 표시 카테고리 → (broad일 때) 장학 감지 → 제목/키워드 규칙 → 기타(기본).
 */
export function classifyNoticeCategory(notice) {
  const raw = String(notice?.category ?? '').trim()

  // 1) 원본 카테고리를 우선 사용한다.
  if (raw === '장학') return '장학'
  if (DISPLAY_CATEGORY_SET.has(raw)) return raw
  if (raw === '시설' || raw === 'IT' || raw === '시설·IT') return '생활·시설' // seed/legacy 매핑
  if (raw === '생활') return '생활·시설'
  if (raw === '창업' || raw === '공모전') return '창업·공모전'

  // 2) '통합공지'처럼 넓은 값은 보조 분류. 원본이 넓지도 않고 미지값이면 그래도 아래로 진행.
  if (isScholarshipNotice(notice)) return '장학'

  const haystack = normalizeForSearch(
    [notice?.title, ...(Array.isArray(notice?.keywords) ? notice.keywords : [])].filter(Boolean).join(' ')
  )
  for (const [cat, keys] of CATEGORY_KEYWORD_RULES) {
    if (keys.some(k => k && haystack.includes(k))) return cat
  }

  // 3) 어느 규칙에도 걸리지 않으면 '기타'
  return '기타'
}

/** 'YYYY-MM-DD...' 문자열을 UTC 자정 timestamp(ms)로 안전 파싱. 잘못된 값이면 null. */
export function parseNoticeDate(raw) {
  const m = String(raw ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const year = Number(m[1]); const month = Number(m[2]); const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const t = Date.UTC(year, month - 1, day)
  return Number.isNaN(t) ? null : t
}

/** 기간 필터 하한 timestamp. 'all'이면 null(제한 없음). */
export function dateRangeCutoff(range, now = Date.now()) {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : null
  if (days == null) return null
  return now - days * 24 * 60 * 60 * 1000
}

/**
 * 공지에서 신뢰성 있는 접수 마감 날짜를 추출한다.
 * 게시일과 혼동하지 않으며, 명확한 마감 표현이 있는 경우에만 반환한다.
 */
export function extractNoticeDeadline(notice, now = Date.now()) {
  if (!notice || typeof notice !== 'object') return null

  // 1) 데이터 모델에 명시적 deadline이 있는 경우
  if (notice.deadline) {
    const parsed = parseNoticeDate(notice.deadline)
    if (parsed) return buildDeadlineResult(parsed, notice.deadline, now)
  }

  // 2) 제목 또는 content에서 명확한 마감일 패턴 추출
  const text = `${notice.title || ''} ${notice.content || ''}`
  if (!text.trim()) return null

  // 기준 연도 추론 (공지 게시일의 연도, 없으면 현재 연도)
  const noticeYear = notice.date ? new Date(notice.date).getFullYear() : new Date(now).getFullYear()

  // 패턴 1: 마감/접수 YYYY.MM.DD 또는 YYYY-MM-DD
  const p1 = text.match(/(?:마감|접수|신청|기한)[^\d\n]{0,10}(\d{4})[./-](\d{1,2})[./-](\d{1,2})/)
  if (p1) {
    const y = Number(p1[1]), m = Number(p1[2]), d = Number(p1[3])
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const t = Date.UTC(y, m - 1, d)
      return buildDeadlineResult(t, dateStr, now)
    }
  }

  // 패턴 2: ~ YYYY.MM.DD 또는 ~ YYYY-MM-DD
  const p2 = text.match(/[~∼-]\s*(\d{4})[./-](\d{1,2})[./-](\d{1,2})/)
  if (p2) {
    const y = Number(p2[1]), m = Number(p2[2]), d = Number(p2[3])
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const t = Date.UTC(y, m - 1, d)
      return buildDeadlineResult(t, dateStr, now)
    }
  }

  // 패턴 3: (~MM.DD) 또는 ~ MM/DD 마감 등
  const p3 = text.match(/[~∼-]\s*(\d{1,2})[./-](\d{1,2})\s*(?:마감|\)|$|\s)/)
  if (p3) {
    const m = Number(p3[1]), d = Number(p3[2])
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const dateStr = `${noticeYear}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const t = Date.UTC(noticeYear, m - 1, d)
      return buildDeadlineResult(t, dateStr, now)
    }
  }

  // 패턴 4: ~ M월 D일 또는 M월 D일 마감
  const p4 = text.match(/(?:[~∼-]\s*(\d{1,2})월\s*(\d{1,2})일|(\d{1,2})월\s*(\d{1,2})일\s*마감)/)
  if (p4) {
    const m = Number(p4[1] || p4[3]), d = Number(p4[2] || p4[4])
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const dateStr = `${noticeYear}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const t = Date.UTC(noticeYear, m - 1, d)
      return buildDeadlineResult(t, dateStr, now)
    }
  }

  return null
}

function buildDeadlineResult(deadlineUtcMs, dateStr, nowMs) {
  // 오늘 날짜의 UTC 자정 계산
  const nowDate = new Date(nowMs)
  const todayUtcMs = Date.UTC(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate())
  const diffDays = Math.round((deadlineUtcMs - todayUtcMs) / (24 * 60 * 60 * 1000))

  let text = ''
  let isClosingSoon = false
  let isOpen = true

  if (diffDays < 0) {
    text = '마감'
    isOpen = false
    isClosingSoon = false
  } else if (diffDays === 0) {
    text = '오늘 마감'
    isOpen = true
    isClosingSoon = true
  } else if (diffDays === 1) {
    text = 'D-1'
    isOpen = true
    isClosingSoon = true
  } else {
    text = `D-${diffDays}`
    isOpen = true
    isClosingSoon = diffDays <= 7
  }

  return {
    deadline: dateStr,
    deadlineTimestamp: deadlineUtcMs,
    diffDays,
    text,
    isClosingSoon,
    isOpen,
  }
}

/**
 * 카드에 노출할 D-day 문자열 ('D-3', '오늘 마감', '마감' 등). 마감일이 없으면 null.
 */
export function formatNoticeDDay(notice, now = Date.now()) {
  const res = extractNoticeDeadline(notice, now)
  return res ? res.text : null
}

/**
 * 'YYYY-MM-DD' 또는 'YYYY.MM.DD' 문자열을 한국어 'M월 D일'로 변환
 */
export function formatKoreanDate(dateStr) {
  if (!dateStr) return ''
  const m = String(dateStr).match(/(?:(\d{4})[./-])?(\d{1,2})[./-](\d{1,2})/)
  if (!m) return dateStr
  const month = Number(m[2])
  const day = Number(m[3])
  if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
    return `${month}월 ${day}일`
  }
  return dateStr
}

/**
 * 공지 상세 화면에 표시할 게시일 형식 변환 (예: '2026년 10월 2일')
 */
export function formatNoticeDetailDate(dateStr) {
  if (!dateStr) return ''
  const m = String(dateStr).match(/(?:(\d{4})(?:년|[./-])\s*)?(\d{1,2})(?:월|[./-])\s*(\d{1,2})/)
  if (!m) return dateStr
  const year = m[1] || ''
  const month = Number(m[2])
  const day = Number(m[3])
  if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
    if (year) {
      return `${year}년 ${month}월 ${day}일`
    }
    return `${month}월 ${day}일`
  }
  return dateStr
}

/**
 * 공지 상세 화면 본문 정제:
 * - HTML 태그 및 엔티티 가독성 변환
 * - 제목 중복 출력 방지
 * - 본문이 없거나 제목만 반복된 경우 빈 문자열 반환
 */
export function getCleanNoticeBody(notice) {
  if (!notice) return ''
  let text = String(notice.body || notice.content || '').trim()
  if (!text) return ''

  if (/<[a-z][\s\S]*>/i.test(text)) {
    text = text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .trim()
  }

  const normTitle = String(notice.title || '').trim().replace(/\s+/g, ' ')
  const normText = text.replace(/\s+/g, ' ')
  if (normText === normTitle) return ''
  if (normTitle && text.startsWith(notice.title.trim())) {
    text = text.slice(notice.title.trim().length).trim()
  }

  return text
}

/**
 * Apple List 스타일 공지 카드에 노출할 단일 메타데이터 라인 반환
 * 우선순위: D-day > NEW > 게시일
 */
export function getNoticeCardMeta(notice, ddayInfo) {
  if (ddayInfo) {
    const isUrgent = ddayInfo.text === '오늘 마감' || ddayInfo.text === 'D-1'
    const dateFormatted = ddayInfo.deadline ? formatKoreanDate(ddayInfo.deadline) : ''
    const text = dateFormatted ? `${ddayInfo.text} · ${dateFormatted} 마감` : ddayInfo.text
    return { text, isUrgent, type: 'dday' }
  }

  if (notice?.isNew) {
    const dateFormatted = notice.date ? formatKoreanDate(notice.date) : ''
    const text = dateFormatted ? `NEW · ${dateFormatted}` : 'NEW'
    return { text, isUrgent: false, type: 'new' }
  }

  const rawDate = String(notice?.date || '').trim()
  const dotDate = rawDate ? rawDate.replace(/-/g, '.') : ''
  return { text: dotDate, isUrgent: false, type: 'date' }
}

export const DEFAULT_NOTICE_FILTERS = {
  tab: 'all',          // 'all' | 'interests' | 'saved'
  category: '전체',
  query: '',
  sort: 'newest',      // 'newest' | 'deadline'
  status: 'all',       // 'all' | 'new' | 'accepting' | 'closingSoon'
  dateRange: 'all',    // 'all' | '7d' | '30d'
  interests: [],       // string[]
  savedKeys: new Set(),// Set<string>
}

/**
 * 카테고리·검색어·상세필터·상위탭을 동시에 적용해 파생 목록을 반환한다. (원본 배열 불변)
 */
export function applyNoticeFilters(notices, filters = {}, now = Date.now()) {
  const list = Array.isArray(notices) ? notices : []
  const {
    tab,
    category,
    query,
    sort,
    status,
    dateRange,
    interests = [],
    savedKeys = new Set(),
  } = { ...DEFAULT_NOTICE_FILTERS, ...filters }

  const q = normalizeForSearch(query)
  const cutoff = dateRangeCutoff(dateRange, now)
  const interestSet = new Set(interests)
  const savedKeySet = savedKeys instanceof Set ? savedKeys : new Set(savedKeys || [])

  const filtered = list.filter(n => {
    const cat = classifyNoticeCategory(n)
    const nKey = noticeReadKey(n)

    // 1. 즐겨찾기 카테고리 또는 saved 탭 (하위 호환)
    if (category === '즐겨찾기' || tab === 'saved') {
      if (!nKey || !savedKeySet.has(nKey)) return false
    } else if (tab === 'interests') {
      if (interestSet.size > 0 && !interestSet.has(cat)) return false
    }

    // 2. 카테고리 필터
    if (category && category !== '전체' && category !== '즐겨찾기' && cat !== category) return false

    // 3. 상태 필터 ('all' | 'new' | 'accepting' | 'closingSoon')
    if (status === 'new' && !n?.isNew) return false
    if (status === 'accepting' || status === 'closingSoon') {
      const dday = extractNoticeDeadline(n, now)
      if (status === 'accepting') {
        if (dday) {
          if (!dday.isOpen) return false
        } else {
          const isRecruiting = /모집|접수|신청|참여|등록/.test(n?.title || '')
          const isClosed = /마감|종료/.test(n?.title || '')
          if (!isRecruiting || isClosed) return false
        }
      } else if (status === 'closingSoon') {
        if (!dday || !dday.isClosingSoon) return false
      }
    }

    // 4. 기간 필터
    if (cutoff != null) {
      const t = parseNoticeDate(n?.date)
      if (t == null || t < cutoff) return false
    }

    // 5. 검색어 필터 (제목 + 카테고리)
    if (q) {
      const inTitle = normalizeForSearch(n?.title).includes(q)
      const inCategory = normalizeForSearch(cat).includes(q)
      if (!inTitle && !inCategory) return false
    }

    return true
  })

  // 정렬 (최신순 / 마감임박순)
  if (sort === 'deadline') {
    return filtered.sort((a, b) => {
      const da = extractNoticeDeadline(a, now)
      const db = extractNoticeDeadline(b, now)
      if (da?.isOpen && db?.isOpen) return da.diffDays - db.diffDays
      if (da?.isOpen && !db?.isOpen) return -1
      if (!da?.isOpen && db?.isOpen) return 1
      const ta = parseNoticeDate(a?.date)
      const tb = parseNoticeDate(b?.date)
      if (ta == null && tb == null) return 0
      if (ta == null) return 1
      if (tb == null) return -1
      return tb - ta
    })
  }

  // 최신순 (기본값)
  return filtered.sort((a, b) => {
    const ta = parseNoticeDate(a?.date)
    const tb = parseNoticeDate(b?.date)
    if (ta == null && tb == null) return 0
    if (ta == null) return 1
    if (tb == null) return -1
    return tb - ta
  })
}

/** 기본값과 다른 상세필터 개수 (필터 아이콘 배지용). 상위탭·카테고리·검색어는 제외. */
export function countActiveDetailFilters(filters = {}) {
  const f = { ...DEFAULT_NOTICE_FILTERS, ...filters }
  let n = 0
  if (f.sort && f.sort !== DEFAULT_NOTICE_FILTERS.sort) n++
  if (f.status && f.status !== DEFAULT_NOTICE_FILTERS.status) n++
  if (f.dateRange && f.dateRange !== DEFAULT_NOTICE_FILTERS.dateRange) n++
  return n
}
